import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import type { EquityDividendFormData } from "@/types/equityDividend";
import { useCurrencyList } from "@/hooks/useCurrencyList";
import { formatAccountLabel } from "./formatAccountLabel";
import {EquitySecurity} from "@/integrations/supabase/equity.models.ts";
import {Account} from "@/integrations/supabase/account.models.ts";

/** EquitySecurityDetail is a full EquitySecurity record (may include transactions). */
export type EquitySecurityDetail = EquitySecurity & {
  transactions?: unknown[];
};

export const equityDividendFormSchema = z.object({
  target_account_id: z.string().uuid("Account is required"),
  amount: z
    .number({ required_error: "Amount is required" })
    .gt(0, "Amount must be greater than 0")
    .max(999_999_999.99, "Amount must not exceed 999,999,999.99"),
  payment_date: z
    .string()
    .min(1, "Payment date is required")
    .refine(
      (d) => d <= new Date().toISOString().slice(0, 10),
      "Payment date must be today or in the past",
    )
    .refine(
      (d) => {
        const date = new Date(d);
        const tenYearsAgo = new Date();
        tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
        return date >= tenYearsAgo;
      },
      "Payment date must not be more than 10 years in the past",
    ),
  currency: z.string().min(1, "Currency is required"),
  notes: z
    .string()
    .max(500, "Notes must not exceed 500 characters")
    .optional(),
});

export type EquityDividendFormValues = z.infer<typeof equityDividendFormSchema>;

export interface EquityDividendFormProps {
  equity: EquitySecurityDetail;
  accounts: Account[];
  accountsLoading: boolean;
  accountsError: boolean;
  onSave: (data: EquityDividendFormData) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export function EquityDividendForm({
  equity,
  accounts,
  accountsLoading,
  accountsError,
  onSave,
  onCancel,
  isSubmitting = false,
}: EquityDividendFormProps) {
  const today = new Date().toISOString().slice(0, 10);
  const tenYearsAgo = new Date();
  tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
  const minDate = tenYearsAgo.toISOString().slice(0, 10);

  const form = useForm<EquityDividendFormValues>({
    resolver: zodResolver(equityDividendFormSchema),
    defaultValues: {
      target_account_id: "",
      amount: undefined,
      payment_date: today,
      currency: equity.currency ?? "",
      notes: "",
    },
  });

  const {
    data: availableCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorLoadingCurrencies,
  } = useCurrencyList();

  const selectedAccountId = form.watch("target_account_id");
  const selectedCurrency = form.watch("currency");
  const selectedAccount = accounts.find((a) => a.id === selectedAccountId);
  const showCurrencyMismatch =
    selectedAccount != null && selectedAccount.currency !== selectedCurrency;

  const noAccounts = !accountsLoading && !accountsError && accounts.length === 0;
  const submitDisabled = isSubmitting || accountsLoading || noAccounts;

  const accountSelectorDisabled = accountsLoading || accountsError || accounts.length === 0;
  const accountPlaceholder = accountsLoading
    ? "Loading accounts…"
    : accounts.length === 0
      ? "No accounts available"
      : "Select account";

  const onSubmit = async (values: EquityDividendFormValues) => {
    try {
      await onSave({
        equity_security_id: equity.id,
        target_account_id: values.target_account_id,
        amount: values.amount,
        currency: values.currency,
        payment_date: values.payment_date,
        notes: values.notes || undefined,
      });
      // Clear form on successful save
      form.reset({
        target_account_id: "",
        amount: undefined,
        payment_date: today,
        currency: equity.currency ?? "",
        notes: "",
      });
    } catch (error) {
      // Error handling is managed by the parent component through toast notifications
      // Re-throw to allow parent error handling
      throw error;
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
        aria-label="Record equity dividend"
      >
        {/* Target Account */}
        <FormField
          control={form.control}
          name="target_account_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Target Account *</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={accountSelectorDisabled}
              >
                <FormControl>
                  <SelectTrigger
                    aria-required="true"
                    aria-label="Select target account"
                  >
                    <SelectValue placeholder={accountPlaceholder} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {formatAccountLabel(account)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {accountsError && (
                <Alert variant="destructive" className="mt-2">
                  <AlertDescription>
                    Failed to load accounts. Please try again.
                  </AlertDescription>
                </Alert>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Amount */}
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Amount *</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="999999999.99"
                  placeholder="e.g. 1500.00"
                  aria-required="true"
                  value={field.value ?? ""}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value === ""
                        ? undefined
                        : parseFloat(e.target.value),
                    )
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Payment Date */}
        <FormField
          control={form.control}
          name="payment_date"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Payment Date *</FormLabel>
              <FormControl>
                <Input
                  type="date"
                  aria-required="true"
                  min={minDate}
                  max={today}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Currency */}
        <FormField
          control={form.control}
          name="currency"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Currency *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger
                    aria-required="true"
                    aria-label="Select currency"
                  >
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {isLoadingCurrencies && (
                    <SelectItem value="__loading__" disabled>
                      Loading currencies…
                    </SelectItem>
                  )}
                  {!isLoadingCurrencies && !errorLoadingCurrencies && availableCurrencies
                    ? availableCurrencies.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))
                    : !isLoadingCurrencies && errorLoadingCurrencies && (
                        <SelectItem value="__error__" disabled>
                          Failed to load currencies
                        </SelectItem>
                      )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Notes */}
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Optional notes about this dividend payment"
                  rows={3}
                  maxLength={500}
                  aria-label="Optional notes"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Currency mismatch warning */}
        {showCurrencyMismatch && (
          <Alert variant="default" role="status" aria-live="polite">
            <AlertDescription>
              The selected account&apos;s currency ({selectedAccount.currency})
              differs from the dividend currency ({selectedCurrency}). The
              amount will be converted automatically.
            </AlertDescription>
          </Alert>
        )}

        {/* No accounts warning */}
        {noAccounts && (
          <p className="text-sm text-muted-foreground" role="status">
            At least one equity security and one account are required to record
            a dividend.
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitDisabled}
            aria-disabled={submitDisabled}
          >
            {isSubmitting && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            )}
            {isSubmitting ? "Saving…" : "Record Dividend"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
